import { useState, useEffect, useCallback } from "react";
import { usePlaidLink } from "react-plaid-link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { 
  Building2, Plus, RefreshCw, Trash2, AlertTriangle, CheckCircle, 
  Loader2, CreditCard, Landmark, ExternalLink, Clock, ArrowRight, 
  Timer, History, Settings2
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import api from "@/lib/api";

function PlaidLinkButton({ companyId, onSuccess }) {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const createLinkToken = async () => {
      try {
        setLoading(true);
        const res = await api.post("/plaid/create-link-token", {
          company_id: companyId
        });
        setLinkToken(res.data.link_token);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to initialize bank connection");
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      createLinkToken();
    }
  }, [companyId]);

  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    try {
      const res = await api.post("/plaid/exchange-token", {
        public_token: publicToken,
        company_id: companyId,
        institution_id: metadata.institution.institution_id,
        institution_name: metadata.institution.name,
        accounts: metadata.accounts.map(acc => ({
          id: acc.id,
          name: acc.name,
          mask: acc.mask,
          type: acc.type,
          subtype: acc.subtype
        }))
      });
      
      toast.success(`Connected to ${metadata.institution.name}!`);
      onSuccess(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to connect bank account");
    }
  }, [companyId, onSuccess]);

  const onPlaidExit = useCallback((error, metadata) => {
    if (error) {
      toast.error(`Connection failed: ${error.error_code}`);
    }
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: onPlaidExit,
  });

  return (
    <Button 
      onClick={() => open()} 
      disabled={!ready || loading}
      className="btn-primary-gradient"
      data-testid="connect-bank-btn"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : (
        <Plus className="w-4 h-4 mr-2" />
      )}
      Connect Bank Account
    </Button>
  );
}

export default function BankConnectionsPage({ companyId, onNavigate }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingId, setSyncingId] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, connection: null });
  
  // Auto-sync settings
  const [autoSyncDialog, setAutoSyncDialog] = useState({ open: false, connection: null });
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [autoSyncFrequency, setAutoSyncFrequency] = useState("daily");
  const [autoSyncSaving, setAutoSyncSaving] = useState(false);
  const [syncFrequencies, setSyncFrequencies] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);

  const loadConnections = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const [connRes, freqRes] = await Promise.all([
        api.get(`/companies/${companyId}/plaid/connections`),
        api.get('/plaid/sync-frequencies')
      ]);
      setConnections(connRes.data);
      setSyncFrequencies(freqRes.data);
    } catch (err) {
      toast.error("Failed to load bank connections");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const handleSync = async (connectionId, institutionName) => {
    setSyncingId(connectionId);
    setSyncResult(null);
    try {
      const res = await api.post(`/companies/${companyId}/plaid/sync/${connectionId}`);
      const { added, modified, removed, classified, has_more } = res.data;
      
      setSyncResult({
        connectionId,
        added,
        modified,
        removed,
        classified,
        has_more,
        institutionName
      });
      
      if (added > 0 || modified > 0) {
        toast.success(
          <div className="flex flex-col gap-1">
            <span className="font-medium">Sync Complete!</span>
            <span className="text-sm opacity-90">
              {added > 0 && `${added} new transaction${added !== 1 ? 's' : ''}`}
              {added > 0 && modified > 0 && ', '}
              {modified > 0 && `${modified} updated`}
              {classified > 0 && ` (${classified} auto-categorized)`}
            </span>
          </div>
        );
      } else {
        toast.info("No new transactions found");
      }
      
      loadConnections();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sync transactions");
      setSyncResult(null);
    } finally {
      setSyncingId(null);
    }
  };

  const handleDelete = async () => {
    const connection = deleteDialog.connection;
    if (!connection) return;
    
    try {
      await api.delete(`/companies/${companyId}/plaid/connections/${connection.connection_id}`);
      toast.success("Connection removed");
      setDeleteDialog({ open: false, connection: null });
      loadConnections();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove connection");
    }
  };

  const openAutoSyncSettings = async (connection) => {
    try {
      const [settingsRes, historyRes] = await Promise.all([
        api.get(`/companies/${companyId}/plaid/connections/${connection.connection_id}/auto-sync`),
        api.get(`/companies/${companyId}/plaid/sync-history?connection_id=${connection.connection_id}&limit=10`)
      ]);
      
      setAutoSyncEnabled(settingsRes.data.auto_sync_enabled);
      setAutoSyncFrequency(settingsRes.data.auto_sync_frequency || "daily");
      setSyncHistory(historyRes.data.history || []);
      setAutoSyncDialog({ open: true, connection });
    } catch (err) {
      toast.error("Failed to load auto-sync settings");
    }
  };

  const saveAutoSyncSettings = async () => {
    if (!autoSyncDialog.connection) return;
    setAutoSyncSaving(true);
    
    try {
      await api.put(
        `/companies/${companyId}/plaid/connections/${autoSyncDialog.connection.connection_id}/auto-sync`,
        null,
        { params: { enabled: autoSyncEnabled, frequency: autoSyncFrequency } }
      );
      
      toast.success(autoSyncEnabled 
        ? `Auto-sync enabled (${syncFrequencies.find(f => f.value === autoSyncFrequency)?.label || autoSyncFrequency})`
        : "Auto-sync disabled"
      );
      
      setAutoSyncDialog({ open: false, connection: null });
      loadConnections();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setAutoSyncSaving(false);
    }
  };

  const formatSyncTime = (isoString) => {
    if (!isoString) return "Never";
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const handleConnectionAdded = () => {
    loadConnections();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-500/20 text-green-400"><CheckCircle className="w-3 h-3 mr-1" /> Active</Badge>;
      case "error":
        return <Badge className="bg-red-500/20 text-red-400"><AlertTriangle className="w-3 h-3 mr-1" /> Error</Badge>;
      case "pending_expiration":
        return <Badge className="bg-yellow-500/20 text-yellow-400"><Clock className="w-3 h-3 mr-1" /> Expiring</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#e85d24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bank-connections-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">Bank Connections</h1>
          <p className="text-[#8b9299] mt-1">Connect your bank accounts to automatically import transactions</p>
        </div>
        <PlaidLinkButton companyId={companyId} onSuccess={handleConnectionAdded} />
      </div>

      {/* Connections List */}
      {connections.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Landmark className="w-16 h-16 text-[#6b7280] mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Bank Connections</h3>
            <p className="text-[#8b9299] text-center max-w-md mb-6">
              Connect your bank accounts to automatically import and categorize your transactions.
            </p>
            <PlaidLinkButton companyId={companyId} onSuccess={handleConnectionAdded} />
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {connections.map((connection) => (
            <Card key={connection.connection_id} className="bg-card border-border" data-testid={`connection-${connection.connection_id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-[#232b3e] flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-[#e85d24]" />
                    </div>
                    <div>
                      <CardTitle className="text-white text-lg">{connection.institution_name}</CardTitle>
                      <CardDescription className="flex items-center gap-2">
                        {getStatusBadge(connection.status)}
                        {connection.last_synced && (
                          <span className="text-xs text-[#6b7280]">
                            Last synced: {new Date(connection.last_synced).toLocaleDateString()}
                          </span>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSync(connection.connection_id, connection.institution_name)}
                      disabled={syncingId === connection.connection_id}
                      data-testid={`sync-btn-${connection.connection_id}`}
                    >
                      {syncingId === connection.connection_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      <span className="ml-2">Sync Transactions</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openAutoSyncSettings(connection)}
                      className={connection.auto_sync_enabled ? "border-green-500/50 text-green-400" : ""}
                      data-testid={`settings-btn-${connection.connection_id}`}
                    >
                      <Settings2 className="w-4 h-4" />
                      {connection.auto_sync_enabled && <Timer className="w-3 h-3 ml-1" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => setDeleteDialog({ open: true, connection })}
                      data-testid={`delete-btn-${connection.connection_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              {connection.error_message && (
                <CardContent className="pt-0 pb-3">
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">{connection.error_message}</p>
                  </div>
                </CardContent>
              )}
              
              {/* Sync Results */}
              {syncResult && syncResult.connectionId === connection.connection_id && (
                <CardContent className="pt-0 pb-3">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <div>
                          <p className="text-green-400 font-medium">Sync Complete</p>
                          <p className="text-green-400/80 text-sm">
                            {syncResult.added > 0 && `${syncResult.added} new`}
                            {syncResult.added > 0 && syncResult.modified > 0 && ', '}
                            {syncResult.modified > 0 && `${syncResult.modified} updated`}
                            {syncResult.added === 0 && syncResult.modified === 0 && 'No new transactions'}
                          </p>
                          {syncResult.classified > 0 && (
                            <p className="text-blue-400/80 text-xs mt-1">
                              AI auto-categorized {syncResult.classified} transaction{syncResult.classified !== 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                      </div>
                      {syncResult.added > 0 && onNavigate && (
                        <Button
                          size="sm"
                          className="btn-primary-gradient"
                          onClick={() => onNavigate('transactions')}
                          data-testid="view-transactions-btn"
                        >
                          View Transactions
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                    </div>
                    {syncResult.has_more && (
                      <p className="text-yellow-400/80 text-xs mt-2">
                        More transactions available. Click sync again to continue.
                      </p>
                    )}
                  </div>
                </CardContent>
              )}
              
              {connection.accounts && connection.accounts.length > 0 && (
                <CardContent className="pt-0">
                  <h4 className="text-sm text-[#8b9299] mb-2">Linked Accounts</h4>
                  <div className="grid gap-2">
                    {connection.accounts.map((account) => (
                      <div 
                        key={account.account_id}
                        className="flex items-center justify-between p-3 bg-[#232b3e] rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          {account.account_type === "credit_card" ? (
                            <CreditCard className="w-5 h-5 text-[#6b7280]" />
                          ) : (
                            <Landmark className="w-5 h-5 text-[#6b7280]" />
                          )}
                          <div>
                            <p className="text-white text-sm font-medium">{account.name}</p>
                            <p className="text-[#6b7280] text-xs">
                              ••••{account.account_number_last4}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white text-sm font-medium">
                            ${(account.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </p>
                          <p className="text-[#6b7280] text-xs capitalize">{account.account_type.replace('_', ' ')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Sandbox Notice */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="flex items-start gap-3 py-4">
          <ExternalLink className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-400 font-medium">Sandbox Mode</p>
            <p className="text-blue-400/80 text-sm mt-1">
              Use test credentials: <code className="bg-blue-500/20 px-1 rounded">user_good</code> / <code className="bg-blue-500/20 px-1 rounded">pass_good</code>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, connection: deleteDialog.connection })}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Remove Bank Connection?</AlertDialogTitle>
            <AlertDialogDescription>
              This will disconnect {deleteDialog.connection?.institution_name} from Corp Ledger. 
              Your existing transactions will be kept, but no new transactions will be imported.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Remove Connection
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Auto-Sync Settings Dialog */}
      <Dialog open={autoSyncDialog.open} onOpenChange={(open) => setAutoSyncDialog({ open, connection: open ? autoSyncDialog.connection : null })}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Timer className="w-5 h-5 text-[#e85d24]" />
              Auto-Sync Settings
            </DialogTitle>
            <DialogDescription>
              {autoSyncDialog.connection?.institution_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label className="text-white">Automatic Sync</Label>
                <p className="text-xs text-[#8b9299]">
                  Automatically sync transactions on a schedule
                </p>
              </div>
              <Switch
                checked={autoSyncEnabled}
                onCheckedChange={setAutoSyncEnabled}
                data-testid="auto-sync-toggle"
              />
            </div>
            
            {/* Frequency Selector */}
            {autoSyncEnabled && (
              <div className="space-y-2">
                <Label className="text-white">Sync Frequency</Label>
                <Select value={autoSyncFrequency} onValueChange={setAutoSyncFrequency}>
                  <SelectTrigger className="bg-[#141b2d] border-border" data-testid="frequency-select">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {syncFrequencies.map((freq) => (
                      <SelectItem key={freq.value} value={freq.value}>
                        <div className="flex flex-col">
                          <span>{freq.label}</span>
                          <span className="text-xs text-[#8b9299]">{freq.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Sync History */}
            {syncHistory.length > 0 && (
              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Recent Sync History
                </Label>
                <div className="bg-[#141b2d] rounded-lg border border-border divide-y divide-border max-h-40 overflow-y-auto">
                  {syncHistory.slice(0, 5).map((entry, idx) => (
                    <div key={idx} className="px-3 py-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-[#8b9299]">{formatSyncTime(entry.timestamp)}</span>
                        <Badge variant={entry.status === "success" ? "default" : "destructive"} className="text-xs">
                          {entry.status === "success" ? (
                            <CheckCircle className="w-3 h-3 mr-1" />
                          ) : (
                            <AlertTriangle className="w-3 h-3 mr-1" />
                          )}
                          {entry.status}
                        </Badge>
                      </div>
                      {entry.status === "success" && (
                        <p className="text-[#6b7280] text-xs mt-1">
                          +{entry.added || 0} new, ~{entry.modified || 0} updated
                          {entry.classified > 0 && `, ${entry.classified} categorized`}
                        </p>
                      )}
                      {entry.status === "error" && entry.error_message && (
                        <p className="text-red-400 text-xs mt-1">{entry.error_message}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoSyncDialog({ open: false, connection: null })}>
              Cancel
            </Button>
            <Button onClick={saveAutoSyncSettings} disabled={autoSyncSaving} data-testid="save-auto-sync-btn">
              {autoSyncSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
